/**
 * Algolia records carry a subset of their fields twice: once under `attributes` and again,
 * byte-identically, under `searchableAttributes` (verified across a full category export — every
 * `searchableAttributes` key exists under `attributes` with an equal value, at both product and
 * variant level). The duplicate exists for ALGOLIA's benefit, not ours: the index's
 * `attributesForFaceting` / `searchableAttributes` settings register the product-level facetable and
 * full-text-searchable paths under `searchableAttributes.*`, so that copy is what Algolia's own
 * filtering and search engine can address.
 *
 * That distinction is load-bearing and cuts both ways:
 *
 *   - Algolia-side `filters` strings (the CMS `algolia_filters_query` / `algolia_query`, the
 *     Product-IDs SKU filters) and the /search facet keys MUST keep using `searchableAttributes.*`.
 *     A filter on `attributes.range` is not an error — it silently matches ZERO records, because the
 *     path is absent from `attributesForFaceting`.
 *   - Everything the server path resolves in memory (helpers/algolia/local-dataset.ts: facet counting,
 *     refinements) reads the CANONICAL `attributes.*` copy, and the SSR fetch never retrieves the
 *     duplicate at all (the backend's slimForCache also drops it).
 *
 * `canonicalAttributePath` is the one place that bridges the two: CMS facet keys are authored against
 * the Algolia-facing spelling (`searchableAttributes.range`, `variants.searchableAttributes.colour`)
 * because that is what the index exposes, and this rewrites them onto the copy the engine actually
 * reads. It is applied where the facet configuration enters the server path (helpers/algolia/
 * listing-facets.ts, used by the preload and the V2 listing section), so `facetNames`, the facet
 * components' `attribute` props and the refinement keys are all canonical and cannot drift apart.
 */
export const canonicalAttributePath = (path: string): string =>
  path
    .split('.')
    .map((segment) => (segment === 'searchableAttributes' ? 'attributes' : segment))
    .join('.');
